package com.meistercharts.charts.pv.roofPlanning

import com.meistercharts.algorithms.layers.LayerPaintingContext
import com.meistercharts.annotations.Zoomed
import com.meistercharts.calc.ChartCalculator
import com.meistercharts.canvas.fill
import com.meistercharts.canvas.interaction.ElementVisualState
import com.meistercharts.canvas.paintable.Paintable
import com.meistercharts.canvas.saved
import com.meistercharts.color.Color
import com.meistercharts.resources.Icons
import it.neckar.geometry.Direction
import it.neckar.geometry.Size
import it.neckar.open.formatting.format
import it.neckar.open.kotlin.lang.abs

/**
 * Paints a module area
 */
class ModuleAreaPainter {
  /**
   * The configuration for the module area painter
   */
  val configuration: Configuration = Configuration()

  /**
   * Paints the module area
   */
  fun paint(
    paintingContext: LayerPaintingContext,
    x: @Zoomed Double,
    y: @Zoomed Double,
    width: @Zoomed Double,
    height: @Zoomed Double,
    moduleArea: ModuleArea,
    chartCalculator: ChartCalculator,
    visualState: ElementVisualState,
  ) {
    val style: ModuleAreaStyle = ModuleAreaStyle.forVisualState(visualState)

    val gc = paintingContext.gc
    gc.lineWidth = 0.5
    style.fillAndStroke.apply(gc)

    // The rect itself
    gc.fillRect(x, y, width, height)

    // Border
    gc.strokeRect(x, y, width, height)

    // Cross
    gc.strokeLine(x, y + height, x + width, y)
    gc.strokeLine(x, y, x + width, y + height)

    // Paint the values
    if (style.showLocationAndSize) {
      gc.fill(Color.white)

      drawMeasures(gc, chartCalculator, x, y, width, height, moduleArea.location.x, moduleArea.location.y, moduleArea.size.width, moduleArea.size.height, configuration.moduleAreaSizeInfoGap)

      // Paint the size (top center)
      gc.fillText(
        text = moduleArea.size.let {
          "${(it.width / 1000.0).format(2)}m x ${(it.height / 1000.0).format(2)}m"
        },
        x = x,
        y = y,
        anchorDirection = Direction.BottomLeft,
        gapHorizontal = configuration.moduleAreaSizeInfoGap,
        gapVertical = configuration.moduleAreaSizeInfoGap,
        maxWidth = width.abs() - configuration.moduleAreaSizeInfoGap * 2,
      )
    }


    //Paint the delete icons
    if (style.showDeleteIcon) {
      gc.saved {
        configuration.deleteIcon.paint(paintingContext, x + width / 2.0, y + height / 2.0)
      }
    }
    if (style.showArmedDeleteIcon) {
      gc.saved {
        configuration.deleteIconArmed.paint(paintingContext, x + width / 2.0, y + height / 2.0)
      }
    }

    //Show the rotate button for manual modules only
    if (style.showRotateIcon) {
      gc.saved {
        configuration.rotateIcon.paintInBoundingBox(paintingContext, x + width, y + height, Direction.TopRight, 10.0)
      }
    }
    //Show the rotate button for manual modules only
    if (style.showArmedRotateIcon) {
      gc.saved {
        configuration.rotateIconArmed.paintInBoundingBox(paintingContext, x + width, y + height, Direction.TopRight, 10.0)
      }
    }
  }


  class Configuration {
    /**
     * The gap within the area size info
     */
    var moduleAreaSizeInfoGap: @Zoomed Double = 3.0

    /**
     * The delete icon that is painted on hover
     */
    val deleteIcon: Paintable = Icons.delete(Size.PX_30)

    /**
     * The delete icon while hovering above is
     */
    val deleteIconArmed: Paintable = Icons.delete(Size.PX_30, Color.red)

    /**
     * The rotate icon that is painted on manual modules on hover
     */
    val rotateIcon: Paintable = Icons.rotate(Size.PX_30)

    /**
     * The rotate icon on manual modules while hovering above it
     */
    val rotateIconArmed: Paintable = Icons.rotate(Size.PX_30, Color.blue)
  }
}
